import { format, formatDistanceToNow } from "date-fns";
import React from "react";

/**
 * Enum defining the different date rendering variants available
 */
export enum DateVariant {
  /** Short local date: "6/15/2023" */
  SHORT = "short",

  /** Long date with day name: "Thursday, 15 June 2023" */
  LONG = "long",

  /** Time only: "2:30:45 PM" */
  TIME = "time",

  /** Relative time: "3 minutes ago" */
  RELATIVE = "relative",

  /** Detailed date with time and timezone: "Thu, Jun 15, 2023, 2:30:45 PM UTC" */
  DETAILED = "detailed",

  /** Medium date with month name: "Jun 15, 2023" */
  MEDIUM = "medium",

  /** ISO string: "2023-06-15T14:30:45.123Z" */
  ISO = "iso",

  /** Short date with time: "6/15/2023, 2:30 PM" */
  SHORT_WITH_TIME = "short_with_time"
}

/**
 * Props for the Date component
 */
export interface DateProps {
  /** The date to render (required) */
  date: Date | null;

  /** The rendering variant to use */
  variant: DateVariant;

  /** Additional options for relative time formatting */
  relativeOptions?: {
    includeSeconds?: boolean;
    addSuffix?: boolean;
  };
}

/**
 * A standardized Date component that renders dates consistently across the application.
 * No styling is applied - only the string representation is returned.
 *
 * @example
 * ```tsx
 * // Short date
 * <Date date={new Date()} variant={DateVariant.SHORT} />
 *
 * // Long date with custom locale
 * <Date date={startDate} variant={DateVariant.LONG} locale="en-GB" />
 *
 * // Relative time
 * <Date date={createdAt} variant={DateVariant.RELATIVE} />
 * ```
 */
export const DateComponent: React.FC<DateProps> = ({
  date,
  variant,
  relativeOptions = { includeSeconds: true, addSuffix: true }
}) => {
  if (!date || !(date instanceof global.Date) || isNaN(date.getTime())) {
    return <>-</>;
  }

  const locale = "en-EN"; // Default locale as we only support en for now

  const formatDate = (): string => {
    switch (variant) {
      case DateVariant.SHORT:
        return date.toLocaleDateString(locale);

      case DateVariant.SHORT_WITH_TIME:
        return date.toLocaleString(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit"
        });

      case DateVariant.LONG:
        return format(date, "iiii, d MMMM yyyy");

      case DateVariant.TIME:
        return format(date, "h:mm:ss a");

      case DateVariant.RELATIVE:
        return formatDistanceToNow(date, relativeOptions);

      case DateVariant.DETAILED:
        return date.toLocaleString(locale, {
          timeZone: "UTC",
          weekday: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
          second: "numeric",
          timeZoneName: "short"
        });

      case DateVariant.MEDIUM:
        return date.toLocaleString(locale, {
          timeZone: "UTC",
          year: "numeric",
          month: "short",
          day: "numeric"
        });

      case DateVariant.ISO:
        return date.toISOString();

      default:
        return date.toLocaleDateString(locale);
    }
  };

  return <>{formatDate()}</>;
};

/**
 * Convenience component for date ranges
 */
export interface DateRangeProps {
  /** Start date */
  startDate: Date;

  /** End date */
  endDate: Date;

  /** The rendering variant to use for both dates */
  variant: DateVariant;

  /** Separator between dates (default: " - ") */
  separator?: string;
}

export const DateRange: React.FC<DateRangeProps> = ({
  startDate,
  endDate,
  variant,
  separator = " - "
}) => {
  return (
    <>
      <DateComponent date={startDate} variant={variant} />
      {separator}
      <DateComponent date={endDate} variant={variant} />
    </>
  );
};
